import type { SceneId, SurfaceKind } from './scenes/types';

/** Everything the visualizer page says. `{…}` are placeholders filled at runtime. */
export interface VisualizerCopy {
  seo: { title: string; description: string };
  hero: { eyebrow: string; title: string; lead: string };
  /** Visually hidden heading of the tool itself. */
  toolTitle: string;
  scene: {
    heading: string;
    /** `name` labels the picker; `label` names the room in the picture's description. */
    options: Record<SceneId, { name: string; label: string }>;
  };
  /** What the stone covers, in a sentence ("… on the countertop"). */
  surfaces: Record<SurfaceKind, string>;
  stone: {
    heading: string;
    searchLabel: string;
    searchPlaceholder: string;
    clearSearch: string;
    colourLabel: string;
    allColours: string;
    listLabel: string;
    loadMore: string;
    loadingMore: string;
    showing: string;
    loading: string;
    empty: { title: string; body: string; reset: string };
    error: { title: string; body: string; retry: string };
    unknown: string;
    sampleName: string;
    details: string;
    selected: string;
  };
  adjust: {
    heading: string;
    scale: string;
    scaleValue: string;
    rotate: string;
    rotateOptions: { r0: string; r90: string };
    seams: string;
    reset: string;
  };
  compare: {
    label: string;
    value: string;
    hint: string;
    plainChip: string;
  };
  actions: {
    heading: string;
    enquire: string;
    copy: string;
    copied: string;
    manualLabel: string;
    manualHint: string;
  };
  stage: {
    /** `{scene}`, `{stone}`, `{surface}`, `{scale}`, `{rotate}`, `{share}` */
    label: string;
    loading: string;
    aGranite: string;
  };
  announce: { scene: string; stone: string; more: string; copied: string };
  note: string;
  how: {
    eyebrow: string;
    title: string;
    steps: readonly { title: string; body: string }[];
    note: string;
    shop: string;
  };
  cta: { title: string; body: string };
  noscript: { body: string; link: string };
}

export const content = {
  seo: {
    title: 'See Granite in a Room Before You Buy',
    description:
      'Try granite slabs on a kitchen countertop, floor, wall or staircase before you buy. Pick a colour, check the look, then ask Shree Balaji Granites for a price.',
  },
  hero: {
    eyebrow: 'See it before you buy it.',
    title: 'Granite visualizer',
    lead: 'Pick a place, pick a granite and see it on your kitchen countertop, floor, wall or stairs. Try black, red, white, pink or brown stone in seconds. Then ask us for a price.',
  },
  toolTitle: 'Try granite in a room',
  scene: {
    heading: 'Pick a place',
    options: {
      kitchen: { name: 'Kitchen countertop', label: 'Kitchen' },
      floor: { name: 'Floor', label: 'Living room' },
      wall: { name: 'Wall', label: 'Wall' },
      staircase: { name: 'Staircase', label: 'Staircase' },
    },
  },
  surfaces: {
    countertop: 'the countertop',
    floor: 'the floor',
    wall: 'the wall',
    stairs: 'the stairs',
  },
  stone: {
    heading: 'Pick a granite',
    searchLabel: 'Search granites',
    searchPlaceholder: 'Type a name or colour',
    clearSearch: 'Clear search',
    colourLabel: 'Colour',
    allColours: 'All colours',
    listLabel: 'Granites',
    loadMore: 'Show more granites',
    loadingMore: 'Loading…',
    showing: 'Showing {shown} of {total}',
    loading: 'Loading granites…',
    empty: {
      title: 'No granite found.',
      body: 'Try another name or colour, or clear the filters.',
      reset: 'Clear filters',
    },
    error: {
      title: 'We could not load the granites.',
      body: 'We show a sample stone for now.',
      retry: 'Try again',
    },
    unknown: 'We could not find that granite, so we picked another one.',
    sampleName: 'Sample stone',
    details: 'See details',
    selected: 'Selected granite',
  },
  adjust: {
    heading: 'Change the pattern',
    scale: 'Pattern size',
    scaleValue: '{value} %',
    rotate: 'Pattern direction',
    rotateOptions: { r0: '0°', r90: '90°' },
    seams: 'Show tile lines',
    reset: 'Reset changes',
  },
  compare: {
    label: 'Compare with a plain surface',
    value: '{stone} % stone, {plain} % plain surface',
    hint: 'Drag the line left or right, or use the arrow keys.',
    plainChip: 'Plain surface',
  },
  actions: {
    heading: 'Like what you see?',
    enquire: 'Ask about this stone',
    copy: 'Copy link',
    copied: 'Link copied',
    manualLabel: 'Link to this view',
    manualHint: 'Select the link and copy it.',
  },
  stage: {
    label:
      '{scene} scene with {stone} on {surface}, scale {scale} %, pattern rotated {rotate}°, stone shown across {share} % of the view',
    loading: 'Loading the stone…',
    aGranite: 'a granite',
  },
  announce: {
    scene: '{scene} selected',
    stone: '{stone} selected',
    more: '{count} more granites loaded',
    copied: 'Link copied',
  },
  note: 'This is only a preview. Real slabs can look a little different.',
  how: {
    eyebrow: 'How it works',
    title: 'Three easy steps',
    steps: [
      {
        title: 'Choose the place',
        body: 'Start with the space you are planning: a kitchen countertop, floor, wall or stairs.',
      },
      {
        title: 'Choose the granite',
        body: 'Search by name or colour. The stone appears on the surface at once.',
      },
      {
        title: 'Change and share',
        body: 'Change the pattern size or direction. Move the line to compare with a plain surface. Then copy the link, WhatsApp us or ask for a price.',
      },
    ],
    note: 'This shows a pattern, not a photo of your slab. Real granite can differ in colour and lines, so ask us to show you the real stone before you decide.',
    shop: 'Go to the shop',
  },
  cta: {
    title: 'Love the look? Let’s make it real.',
    body: 'Tell us your plan and the granite you picked. We will help you with samples and a price.',
  },
  noscript: {
    body: 'This tool needs JavaScript. Please turn it on. You can still see every granite in the shop, and ask us for a price.',
    link: 'Go to the shop',
  },
} satisfies VisualizerCopy;
